import { Cart } from '@troop-themes/main';

const HOMEWILD_CART_CONFIG_SELECTOR = '[data-homewild-cart-config]';

const originalCartSwapInNewContent = Cart.prototype.swapInNewContent;

Cart.prototype.swapInNewContent = function swapInNewContent(nextForm) {
  const currentUpsell = this.querySelector('.cart--body homewild-cart-upsell');
  const incomingUpsell = nextForm?.querySelector('.cart--body homewild-cart-upsell');

  if (currentUpsell && incomingUpsell) incomingUpsell.replaceWith(currentUpsell);

  return originalCartSwapInNewContent.call(this, nextForm);
};

const originalCartOpenDrawer = Cart.openDrawer.bind(Cart);

Cart.openDrawer = function openDrawer(trigger) {
  const viewport = document.querySelector('.layout--viewport');
  const cartIsOpen = viewport?.dataset.drawerView === 'cart-drawer'
    && viewport.dataset.drawerStatus?.includes('open');

  if (cartIsOpen) return;

  originalCartOpenDrawer(trigger);
};

function openCartDrawerNow(trigger) {
  const viewport = document.querySelector('.layout--viewport');
  const cartIsOpen = viewport?.dataset.drawerView === 'cart-drawer'
    && viewport.dataset.drawerStatus?.includes('open');

  if (cartIsOpen || typeof window.trigger !== 'function') return;

  window.trigger('theme:drawer:open', {
    side: 'right',
    view: 'cart-drawer',
    trigger
  });
}

const originalCartAddItems = Cart.addItems.bind(Cart);

Cart.addItems = async function addItems(options = {}, trigger = false) {
  if (trigger) openCartDrawerNow(trigger);
  return originalCartAddItems(options, trigger);
};

function getHomewildCartConfig() {
  const source = document.querySelector(HOMEWILD_CART_CONFIG_SELECTOR);
  if (!source) return null;

  try {
    return JSON.parse(source.textContent);
  } catch (error) {
    console.warn('Homewild cart drawer settings could not be read.', error);
    return null;
  }
}

function setPixels(element, property, value) {
  const number = Number(value);
  if (element && Number.isFinite(number)) element.style.setProperty(property, `${number}px`);
}

function getHomewildComponent(config, type) {
  return Array.isArray(config?.components)
    ? config.components.find((component) => component.type === type)
    : null;
}

function componentValue(component, key, fallback) {
  const value = component?.[key];
  return value === undefined || value === null || value === '' ? fallback : value;
}

function orderHomewildComponents(container, components, nodes) {
  if (!container || !Array.isArray(components)) return;

  const desiredNodes = components
    .map((component) => nodes[component.type])
    .filter((node, index, list) => (
      node &&
      node.parentElement === container &&
      list.indexOf(node) === index
    ));

  const currentNodes = [...container.children]
    .filter((node) => desiredNodes.includes(node));

  const alreadyOrdered = desiredNodes.every(
    (node, index) => currentNodes[index] === node
  );

  if (alreadyOrdered || typeof container.moveBefore !== 'function') return;

  desiredNodes.forEach((node) => container.moveBefore(node, null));
}

function formatCartMoney(cents, currency) {
  try {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 2
    }).format(cents / 100);
  } catch (_error) {
    return `$${(cents / 100).toFixed(2)}`;
  }
}

function rewardIcon(name, unlocked) {
  if (unlocked) return '✓';
  if (name === 'truck') return '🚚';
  if (name === 'gift') return '◆';
  if (name === 'check') return '✓';
  return '%';
}

function ensureRewardsElement(cart) {
  let rewards = cart.querySelector('.homewild-rewards');
  if (rewards) return rewards;

  rewards = document.createElement('div');
  rewards.className = 'cart--shipping homewild-rewards';
  rewards.innerHTML = '<p class="homewild-rewards__message" data-item="paragraph" aria-live="polite"></p><div class="homewild-rewards__track" role="progressbar" aria-label="Cart rewards progress" aria-valuemin="0" aria-valuemax="100"><span class="homewild-rewards__fill"></span></div>';
  cart.querySelector('.homewild-cart-header')?.insertAdjacentElement('afterend', rewards);
  return rewards;
}

function renderRewards(cart, config) {
  const rewards = ensureRewardsElement(cart);
  const milestones = (config.rewards || [])
    .filter((reward) => reward.type !== 'shipping' || config.freeShippingAvailable !== false)
    .map((reward) => ({ ...reward, cents: Math.round(Number(reward.threshold) * 100) }))
    .filter((reward) => Number.isFinite(reward.cents) && reward.cents > 0)
    .sort((first, second) => first.cents - second.cents);

  if (!config.progressEnabled || milestones.length === 0) {
    rewards.hidden = true;
    return;
  }

  rewards.hidden = false;
  const subtotal = Number(rewards.dataset.cartSubtotal || cart.dataset.cartSubtotal || 0);
  const maximum = milestones[milestones.length - 1].cents;
  const progress = Math.min(100, Math.max(0, subtotal / maximum * 100));
  const nextReward = milestones.find((reward) => subtotal < reward.cents);
  const message = nextReward
    ? config.nextRewardMessage
      .replace('[amount]', formatCartMoney(nextReward.cents - subtotal, config.currency))
      .replace('[reward]', nextReward.label)
    : config.allRewardsMessage;

  const messageElement = rewards.querySelector('.homewild-rewards__message');
  const track = rewards.querySelector('.homewild-rewards__track');
  if (!messageElement || !track) return;

  messageElement.textContent = message;
  track.setAttribute('aria-valuenow', String(Math.round(progress)));
  track.replaceChildren();

  const fill = document.createElement('span');
  fill.className = 'homewild-rewards__fill';
  fill.style.setProperty('--homewild-progress', `${progress}%`);
  track.append(fill);

  milestones.forEach((reward) => {
    const unlocked = subtotal >= reward.cents;
    const milestone = document.createElement('span');
    milestone.className = 'homewild-rewards__milestone';
    milestone.dataset.unlocked = String(unlocked);
    milestone.style.setProperty('--homewild-position', `${Math.min(100, reward.cents / maximum * 100)}%`);
    milestone.innerHTML = '<span class="homewild-rewards__icon" aria-hidden="true"></span><span class="homewild-rewards__label"></span>';
    milestone.querySelector('.homewild-rewards__icon').textContent = rewardIcon(reward.icon, unlocked);
    milestone.querySelector('.homewild-rewards__label').textContent = reward.label;
    track.append(milestone);
  });
}

function syncDrawerCheckoutFromSection(event) {
  const sectionHtml = event.detail?.section;
  if (typeof sectionHtml !== 'string' || !sectionHtml) return;

  const incomingDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const incomingCart = incomingDocument.querySelector('.cart--root[data-view="drawer"]');
  const currentCart = document.querySelector('.cart--root[data-view="drawer"]');
  if (!incomingCart || !currentCart) return;

  const incomingPrice = incomingCart.querySelector('.cart--checkout-button .money');
  const currentPrice = currentCart.querySelector('.cart--checkout-button .money');
  if (incomingPrice && currentPrice) currentPrice.textContent = incomingPrice.textContent.trim();

  if (incomingCart.dataset.cartSubtotal) {
    currentCart.dataset.cartSubtotal = incomingCart.dataset.cartSubtotal;
  }
}

function applyCartSettings() {
  const config = getHomewildCartConfig();
  if (!config) return;

  const cart = document.querySelector('.cart--root[data-view="drawer"]');
  if (!cart) return;

  const drawer = cart.closest('.drawer--container[data-view="cart-drawer"]');
  const drawerSide = drawer?.closest('.drawer--side[data-side="right"]');
  const progressComponent = getHomewildComponent(config, 'progress');
  const itemsComponent = getHomewildComponent(config, 'cart_items');
  const upsellComponent = getHomewildComponent(config, 'upsell');
  const summaryComponent = getHomewildComponent(config, 'summary');
  const checkoutComponent = getHomewildComponent(config, 'checkout');
  const effectiveUpsell = {
    ...config.upsell,
    enabled: componentValue(upsellComponent, 'enabled', config.upsell?.enabled),
    mode: componentValue(upsellComponent, 'mode', config.upsell?.mode),
    heading: componentValue(upsellComponent, 'heading', config.upsell?.heading),
    intent: componentValue(upsellComponent, 'intent', config.upsell?.intent),
    limit: componentValue(upsellComponent, 'limit', config.upsell?.limit),
    showRating: componentValue(upsellComponent, 'showRating', config.upsell?.showRating),
    buttonLabel: componentValue(upsellComponent, 'buttonLabel', config.upsell?.buttonLabel),
    headingSize: componentValue(upsellComponent, 'headingSize', config.upsell?.headingSize),
    titleSize: componentValue(upsellComponent, 'titleSize', config.upsell?.titleSize),
    priceSize: componentValue(upsellComponent, 'priceSize', config.upsell?.priceSize),
    imageSize: componentValue(upsellComponent, 'imageSize', config.upsell?.imageSize)
  };
  const effectiveProgress = {
    ...config,
    progressEnabled: componentValue(progressComponent, 'enabled', config.progressEnabled),
    progressMessageSize: componentValue(progressComponent, 'messageSize', config.progressMessageSize),
    progressBarHeight: componentValue(progressComponent, 'barHeight', config.progressBarHeight),
    nextRewardMessage: componentValue(progressComponent, 'nextMessage', config.nextRewardMessage),
    allRewardsMessage: componentValue(progressComponent, 'completeMessage', config.allRewardsMessage)
  };
  setPixels(drawer, '--homewild-drawer-width', config.drawerWidth);
  setPixels(drawerSide, '--homewild-drawer-width', config.drawerWidth);
  setPixels(cart, '--homewild-cart-padding', config.drawerPadding);
  setPixels(cart, '--homewild-header-height', config.headerHeight);
  setPixels(cart, '--homewild-cart-logo-width', config.logoWidth);
  setPixels(cart, '--homewild-title-size', config.titleSize);
  setPixels(cart, '--homewild-item-image-size', componentValue(itemsComponent, 'imageSize', config.itemImageSize));
  setPixels(cart, '--homewild-item-title-size', componentValue(itemsComponent, 'titleSize', config.itemTitleSize));
  setPixels(cart, '--homewild-item-price-size', componentValue(itemsComponent, 'priceSize', config.itemPriceSize));
  setPixels(cart, '--homewild-quantity-size', componentValue(itemsComponent, 'quantitySize', config.quantitySize));
  setPixels(cart, '--homewild-item-padding-y', componentValue(itemsComponent, 'paddingY', 16));
  setPixels(cart, '--homewild-progress-message-size', effectiveProgress.progressMessageSize);
  setPixels(cart, '--homewild-progress-height', effectiveProgress.progressBarHeight);
  setPixels(cart, '--homewild-progress-padding-top', componentValue(progressComponent, 'paddingTop', 16));
  setPixels(cart, '--homewild-progress-padding-bottom', componentValue(progressComponent, 'paddingBottom', 46));
  setPixels(cart, '--homewild-upsell-heading-size', effectiveUpsell.headingSize);
  setPixels(cart, '--homewild-upsell-title-size', effectiveUpsell.titleSize);
  setPixels(cart, '--homewild-upsell-price-size', effectiveUpsell.priceSize);
  setPixels(cart, '--homewild-upsell-image-size', effectiveUpsell.imageSize);
  setPixels(cart, '--homewild-upsell-padding-y', componentValue(upsellComponent, 'paddingY', 18));
  setPixels(cart, '--homewild-subtotal-size', componentValue(summaryComponent, 'subtotalSize', config.summary?.subtotalSize));
  setPixels(cart, '--homewild-total-size', config.summary?.totalSize);
  setPixels(cart, '--homewild-checkout-size', componentValue(checkoutComponent, 'textSize', config.summary?.checkoutSize));
  setPixels(cart, '--homewild-checkout-height', componentValue(checkoutComponent, 'height', 52));
  cart.style.setProperty('--homewild-cart-bg', config.background);
  cart.style.setProperty('--homewild-cart-text', config.textColor);
  cart.style.setProperty('--homewild-cart-accent', config.accent);

  const logo = cart.querySelector('.homewild-cart-header__logo');
  if (logo) {
    logo.hidden = !config.showLogo || !config.logoUrl;
    let image = logo.querySelector('img');
    if (config.logoUrl && !image) {
      image = document.createElement('img');
      image.alt = '';
      image.loading = 'eager';
      logo.append(image);
    }
    if (image && config.logoUrl) image.src = config.logoUrl;
  }

  const title = cart.querySelector('.cart--title');
  if (title) {
    title.hidden = !config.showTitle;
    title.textContent = config.title || 'Your cart';
  }

  const subtotalLabel = cart.querySelector('[data-homewild-subtotal-label]');
  const totalLabel = cart.querySelector('[data-homewild-total-label]');
  const checkoutLabel = cart.querySelector('[data-homewild-checkout-label]');
  if (subtotalLabel) subtotalLabel.textContent = `${componentValue(summaryComponent, 'subtotalLabel', config.summary?.subtotalLabel || 'Subtotal')}:`;
  if (totalLabel) totalLabel.textContent = `${config.summary?.totalLabel || 'Total'}:`;
  if (checkoutLabel) {
    checkoutLabel.textContent = componentValue(checkoutComponent, 'label', config.summary?.checkoutLabel || 'Checkout');
  }

  const cartItems = cart.querySelector('[data-homewild-cart-items]');
  if (cartItems) cartItems.hidden = itemsComponent?.enabled === false;

  const summary = cart.querySelector('[data-homewild-cart-summary]');
  if (summary) summary.hidden = summaryComponent?.enabled === false;

  const drawerSubtotal = cart.querySelector('[data-homewild-drawer-subtotal]');
  if (drawerSubtotal) drawerSubtotal.hidden = summaryComponent?.showSubtotal !== true;

  const discounts = cart.querySelector('[data-homewild-cart-discounts]');
  if (discounts) discounts.hidden = summaryComponent?.showDiscounts === false;

  const checkout = cart.querySelector('[data-homewild-cart-checkout]');
  if (checkout) checkout.hidden = checkoutComponent?.enabled === false;

  const checkoutPrice = cart.querySelector('[data-homewild-checkout-price]');
  if (checkoutPrice) checkoutPrice.hidden = checkoutComponent?.showTotal === false;

  renderRewards(cart, effectiveProgress);

  const upsell = cart.querySelector('homewild-cart-upsell');
  if (upsell) upsell.configure(effectiveUpsell);

  const cartBody = cart.querySelector('.cart--body');
  orderHomewildComponents(cartBody, config.components, {
    cart_items: cartItems,
    upsell
  });

  const cartSummary = cart.querySelector('.cart--summary');
  orderHomewildComponents(cartSummary, config.components, {
    summary,
    checkout
  });
}

class HomewildCartUpsell extends HTMLElement {
  connectedCallback() {
    this.handleDrawerOpening = this.handleDrawerOpening.bind(this);
    window.addEventListener('theme:drawer:right:opening', this.handleDrawerOpening);
    this.configure(getHomewildCartConfig()?.upsell);
  }

  disconnectedCallback() {
    window.removeEventListener('theme:drawer:right:opening', this.handleDrawerOpening);
  }

  configure(settings) {
    const previousKey = this.settingsKey;
    this.settings = settings || {};
    this.settingsKey = JSON.stringify(this.settings);
    this.hidden = this.settings.enabled === false;
    if (this.hidden) return;

    if (this.dataset.loaded === 'true' && previousKey && previousKey !== this.settingsKey) {
      delete this.dataset.loaded;
      this.replaceChildren();
    }

    if (this.dataset.loaded === 'true') this.decorate();

    const viewport = document.querySelector('.layout--viewport');
    if (viewport?.dataset.drawerView === 'cart-drawer' && viewport.dataset.drawerStatus?.includes('open')) {
      this.load();
    }
  }

  handleDrawerOpening() {
    const viewport = document.querySelector('.layout--viewport');
    if (viewport?.dataset.drawerView === 'cart-drawer') {
      applyCartSettings();
      this.load();
    }
  }

  decorate() {
    const heading = this.querySelector('.homewild-upsell__heading');
    if (heading) heading.textContent = this.settings.heading || 'Frequently bought together';

    const section = this.querySelector('.homewild-upsell');
    if (section) section.dataset.showRating = String(this.settings.showRating !== false);

    this.querySelectorAll('product-quick-add[data-form="false"] .homewild-upsell-card__button-text').forEach((label) => {
      label.textContent = this.settings.buttonLabel || 'Add';
    });

  }

  loadManual() {
    const template = document.querySelector('[data-homewild-manual-upsells]');
    if (!template) return false;

    const content = template.content.cloneNode(true);
    [...content.querySelectorAll('.homewild-upsell-card[data-product-id]')]
      .slice(3)
      .forEach((card) => card.remove());

    if (!content.querySelector('.homewild-upsell-card')) return false;
    this.replaceChildren(content);
    return true;
  }

  load() {
    if (this.hidden || this.dataset.loaded === 'true' || this.dataset.loading === 'true') return;
    this.dataset.loading = 'true';

    try {
      const loaded = this.loadManual();
      if (!loaded) {
        this.replaceChildren();
        this.hidden = true;
        this.dataset.loaded = 'true';
        return;
      }

      this.hidden = false;
      this.dataset.loaded = 'true';
      this.decorate();
    } catch (error) {
      console.warn(error);
      this.replaceChildren();
      this.hidden = true;
    } finally {
      delete this.dataset.loading;
    }
  }
}

if (!customElements.get('homewild-cart-upsell')) {
  customElements.define('homewild-cart-upsell', HomewildCartUpsell);
}

function openCartDrawerOptimistically(event) {
  if (!(event.target instanceof Element)) return;
  const button = event.target.closest('.product-buy-buttons--primary, .product-quick-add--button');
  if (!button || button.disabled || button.getAttribute('aria-busy') === 'true') return;
  if (button.closest('.drawer--container[data-view="cart-drawer"]')) return;

  const buyButtons = button.closest('product-buy-buttons-element');
  if (buyButtons && buyButtons.dataset.cartType !== 'drawer') return;

  const quickAdd = button.closest('product-quick-add');
  if (quickAdd && quickAdd.dataset.form === 'true') return;

  openCartDrawerNow(button);
}

document.addEventListener('DOMContentLoaded', applyCartSettings);
document.addEventListener('click', openCartDrawerOptimistically, true);
window.addEventListener('theme:cart:drawer:updateHtml', syncDrawerCheckoutFromSection);
window.addEventListener('theme:cart:updated', () => requestAnimationFrame(applyCartSettings));
document.addEventListener('shopify:section:load', () => {
  requestAnimationFrame(() => {
    const upsell = document.querySelector('.cart--root[data-view="drawer"] homewild-cart-upsell');
    if (upsell) {
      delete upsell.dataset.loaded;
      upsell.replaceChildren();
      upsell.hidden = false;
    }
    applyCartSettings();
  });
});
